import { forwardRef, useEffect, useMemo, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { MapPinHouse } from "lucide-react";
import { StepProps, StepRef } from "../../types";
import { StepHeader } from "../../components/StepHeader";
import { NextStepButton } from "../../components/NextStepButton";
import AddressAutocomplete, {
  Location,
} from "components/common/AddressAutocomplete";
import { useGetMemberDetails, useUpdateMembersFamily } from "services/member";

const EMPTY_LOCATION: Location = {
  latitude: 0,
  longitude: 0,
  address: "",
  unit: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  formattedAddress: "",
};

const parseAddress = (address?: string): Location => {
  if (!address) return EMPTY_LOCATION;
  try {
    return JSON.parse(address) ?? EMPTY_LOCATION;
  } catch (_error) {
    return EMPTY_LOCATION;
  }
};

export default forwardRef<StepRef, StepProps>(
  ({ onNext, disabled, signupState }, _ref) => {
    const [address, setAddress] = useState<Location>(
      parseAddress(signupState.userInfo?.address),
    );
    const [savedAddress, setSavedAddress] = useState("");
    const [error, setError] = useState("");
    const { data: memberDetails, request: getMemberDetails } =
      useGetMemberDetails();
    const { request: updateFamilyRequest, loading: saveLoading } =
      useUpdateMembersFamily();

    useEffect(() => {
      getMemberDetails();
    }, []);

    useEffect(() => {
      if (memberDetails?.family?.address) {
        const parsed = parseAddress(memberDetails.family.address);
        setAddress((current) => (current.formattedAddress ? current : parsed));
        setSavedAddress(parsed.formattedAddress || "");
      }
    }, [memberDetails?.family?.address]);

    const hasValue = !!address.formattedAddress;
    const hasExistingAddress = useMemo(
      () => !!savedAddress && savedAddress === address.formattedAddress,
      [address.formattedAddress, savedAddress],
    );

    const handleContinue = async () => {
      if (!hasValue) {
        await onNext?.();
        return;
      }

      if (!memberDetails?.family?.ref) {
        setError("We couldn’t load your household yet. Please try again.");
        return;
      }

      setError("");
      await updateFamilyRequest({
        familyRef: memberDetails.family.ref,
        updatedAddress: JSON.stringify(address),
        newMembers: [],
        updatedMembers: [],
      });

      await onNext?.({
        userInfo: {
          email: signupState.userInfo?.email || "",
          firstName: signupState.userInfo?.firstName || "",
          lastName: signupState.userInfo?.lastName || "",
          phone: signupState.userInfo?.phone || "",
          timezone: signupState.userInfo?.timezone || "",
          address: JSON.stringify(address),
        },
      });
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "24px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "560px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <StepHeader
            title="Share your address"
            subtitle={
              <>
                <Typography variant="body">
                  This helps your agent research things around you, check nearby
                  weather, and give you realistic distances for errands, events,
                  and reservations.
                </Typography>
              </>
            }
            alignment="left"
          />

          <Box
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "16px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <MapPinHouse color="#198282" />
              <Typography variant="bodyHeavy">
                Optional, but especially helpful for adding local insights.
              </Typography>
            </Box>
            <AddressAutocomplete
              label="Home address"
              name="street-address"
              initialValue={address.formattedAddress}
              setValue={(location) => setAddress(location)}
            />
            {hasExistingAddress && (
              <Alert severity="success" sx={{ borderRadius: "12px" }}>
                Your current household address is already saved.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {error}
              </Alert>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <NextStepButton
              skippable={true}
              hasValue={hasValue}
              disabled={disabled || saveLoading}
              continueClick={handleContinue}
              buttonText={hasValue ? "Continue" : "Skip for now"}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);
